import * as signalR from '@microsoft/signalr';

export interface SignalREvents {
  onMessageReceived?: (data: { sessionId: string; message: string }) => void;
  onAgentThinking?: (data: { sessionId: string }) => void;
  onTokenReceived?: (data: { sessionId: string; token: string }) => void;
  onToolCall?: (data: { sessionId: string; toolName: string; arguments: string }) => void;
  onResponseComplete?: (data: { sessionId: string; response: string }) => void;
  onExecutionError?: (data: { sessionId: string; errorMessage: string; source?: string; timestamp?: string }) => void;
  onSubAgentStarted?: (data: { sessionId: string; callId: string; alias: string; depth: number; task: string }) => void;
  onSubAgentTokenReceived?: (data: { sessionId: string; callId: string; token: string }) => void;
  onSubAgentToolCall?: (data: { sessionId: string; callId: string; toolName: string; arguments: string }) => void;
  onSubAgentCompleted?: (data: { sessionId: string; callId: string; result: string; success: boolean; errorMessage?: string }) => void;
}

/**
 * Service for SignalR connection management.
 */
class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private events: SignalREvents = {};
  private _onReconnecting: (() => void) | null = null;
  private _onReconnected: (() => void) | null = null;

  /**
   * Connects to the SignalR hub.
   */
  async connect(baseUrl: string, token: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${baseUrl}/hubs/agentExecution`, {
          accessTokenFactory: () => token,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
              return this.reconnectDelay * Math.pow(2, retryContext.previousRetryCount);
            }
            return null; // Stop reconnecting
          },
        })
        .build();

      // Set up event handlers
      this.setupEventHandlers();

      // Handle connection state changes
      this.connection.onclose((error) => {
        console.log('SignalR connection closed', error);
        this.isConnecting = false;
        if (error) {
          this.attemptReconnect(baseUrl, token);
        }
      });

      this.connection.onreconnecting((error) => {
        console.log('SignalR reconnecting', error);
        this._onReconnecting?.();
      });

      this.connection.onreconnected((connectionId) => {
        console.log('SignalR reconnected', connectionId);
        this.reconnectAttempts = 0;
        this._onReconnected?.();
      });

      await this.connection.start();
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      console.log('SignalR connected');
    } catch (error) {
      this.isConnecting = false;
      console.error('Error connecting to SignalR', error);
      throw error;
    }
  }

  /**
   * Disconnects from the SignalR hub.
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.events = {};
    }
  }

  /**
   * Joins a session group.
   */
  async joinSession(sessionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('JoinSession', sessionId);
    }
  }

  /**
   * Leaves a session group.
   */
  async leaveSession(sessionId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('LeaveSession', sessionId);
    }
  }

  /**
   * Registers event handlers.
   */
  on(events: SignalREvents): void {
    this.events = { ...this.events, ...events };
  }

  /**
   * Unregisters all event handlers.
   */
  off(): void {
    this.events = {};
  }

  /**
   * Gets the connection state.
   */
  getState(): signalR.HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  /**
   * Sets callbacks for connection state transitions triggered by
   * the built-in automatic reconnect mechanism.
   */
  setReconnectionCallbacks(
    onReconnecting: (() => void) | null,
    onReconnected: (() => void) | null,
  ): void {
    this._onReconnecting = onReconnecting;
    this._onReconnected = onReconnected;
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    this.connection.on('MessageReceived', (data: { sessionId: string; message: string }) => {
      this.events.onMessageReceived?.(data);
    });

    this.connection.on('AgentThinking', (data: { sessionId: string }) => {
      this.events.onAgentThinking?.(data);
    });

    this.connection.on('TokenReceived', (data: { sessionId: string; token: string }) => {
      this.events.onTokenReceived?.(data);
    });

    this.connection.on('ToolCall', (data: { sessionId: string; toolName: string; arguments: string }) => {
      this.events.onToolCall?.(data);
    });

    this.connection.on('ResponseComplete', (data: { sessionId: string; response: string }) => {
      this.events.onResponseComplete?.(data);
    });

    this.connection.on('ExecutionError', (data: { sessionId: string; errorMessage: string; source?: string; timestamp?: string }) => {
      this.events.onExecutionError?.(data);
    });

    this.connection.on('SubAgentStarted', (data: { sessionId: string; callId: string; alias: string; depth: number; task: string }) => {
      this.events.onSubAgentStarted?.(data);
    });

    this.connection.on('SubAgentTokenReceived', (data: { sessionId: string; callId: string; token: string }) => {
      this.events.onSubAgentTokenReceived?.(data);
    });

    this.connection.on('SubAgentToolCall', (data: { sessionId: string; callId: string; toolName: string; arguments: string }) => {
      this.events.onSubAgentToolCall?.(data);
    });

    this.connection.on('SubAgentCompleted', (data: { sessionId: string; callId: string; result: string; success: boolean; errorMessage?: string }) => {
      this.events.onSubAgentCompleted?.(data);
    });
  }

  private async attemptReconnect(baseUrl: string, token: string): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    await new Promise((resolve) => setTimeout(resolve, this.reconnectDelay * this.reconnectAttempts));

    try {
      await this.connect(baseUrl, token);
    } catch (error) {
      console.error('Reconnection failed', error);
    }
  }
}

export const signalRService = new SignalRService();
